import {
  ArrowsClockwise,
  ClockClockwise,
  Confetti,
  MapPin,
  Timer,
} from '@phosphor-icons/react';
import BButton from 'components/common/BButton';
import BDivider from 'components/common/BDivider';
import BSpinnerInline from 'components/common/BSpinnerInline';
import CurrencyText from 'components/common/CurrencyText';
import ModalWithActivator from 'components/common/ModalWithActivator';
import RatingStar from 'components/common/RatingStar';
import BDatePicker from 'components/Form/BDatePicker';
import BStepperInput from 'components/Form/BStepperInput';
import PriceWidgetExtraSection from 'components/Vehicle/VehicleDetail/PriceWidget/PriceWidgetExtraSection';
import PriceWidgetExtrasEmptyView from 'components/Vehicle/VehicleDetail/PriceWidget/PriceWidgetExtrasEmptyView';
import { ExtraUnitType } from 'enums/extraUnitTypes';
import { useForm } from 'hooks/useForm';
import { useLocale } from 'hooks/useLocale';
import { useQueryParams } from 'hooks/useQueryParams';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { dateService } from 'services/DateService';
import { experienceTicketService } from 'services/Experience/ExperienceTicketService';
import { experienceService } from 'services/ExperienceService';
import { useVehicleDetailCommentsModal } from 'states/useVehicleDetailCommentsModalState';
import {
  ExperienceDetail,
  ExperiencePriceWidgetState,
} from 'types/experience/experience-detail.type';
import {
  ExperienceCalculatePricingResponse,
  ExperienceSession,
} from 'types/experience/experience-session.type';
import { useDebouncedCallback } from 'use-debounce';
import { getExperienceDurationByTime } from 'utils/experience/experienceDuration.util';
import { getLocalePrefix } from 'utils/getLocalePrefix';
import { noop } from 'utils/noop';

interface ExperiencePriceWidgetBodyProps {
  experience: ExperienceDetail;
  sessions: ExperienceSession[];
  isMobile?: boolean;
}

const ExperiencePriceWidgetBody: FunctionComponent<
  ExperiencePriceWidgetBodyProps
> = ({ experience, sessions, isMobile = false }) => {
  const { locale } = useLocale();
  const { t } = useTranslation('experience');
  const { hour, minute } = getExperienceDurationByTime(experience.duration);
  const { addQueryParam, getQueryParam } = useQueryParams();
  const { setCommentModalVisible } = useVehicleDetailCommentsModal();

  const [_, setIsCalculatingPricing] = useState(false);
  const [calculatedPricing, setCalculatedPricing] =
    useState<ExperienceCalculatePricingResponse | null>(null);

  const calculatePricing = async (newState: ExperiencePriceWidgetState) => {
    try {
      const extras: { sessionExtraId: number }[] = [];

      if (newState.selectedExtras.length) {
        const selectedSession = newState.selectedSession;
        newState.selectedExtras.forEach((e) => {
          const foundExtra = selectedSession.extras.find((a) => a.id === e);
          if (foundExtra) {
            extras.push({
              sessionExtraId: foundExtra.experienceExtraServiceId,
            });
          }
        });
      }

      setIsCalculatingPricing(true);
      const pricing = await experienceService.calculateExperiencePricing({
        sessionId: newState.selectedSession.id,
        ticketCount: newState.ticketCount,
        selectedExtras: extras,
      });
      setCalculatedPricing(pricing);
    } finally {
      setIsCalculatingPricing(false);
    }
  };

  const availableSessions = sessions.filter((a) => {
    return (
      a.availableCapacity > 0 &&
      a.availableCapacity >= a.minimumCapacity &&
      Date.now() <
        dateService
          .formatServerDateToLocalDate(a.dateTimeRange.startTime)
          .getTime()
    );
  });

  const dateToSessionMap = useMemo(() => {
    return availableSessions.reduce((acc, session) => {
      acc[
        dateService.getFormattedDate(
          dateService.formatServerDateToLocalDate(
            session.dateTimeRange.startTime
          )
        )
      ] = session;
      return acc;
    }, {} as Record<string, ExperienceSession>);
  }, [availableSessions]);

  const firstSession = availableSessions.length
    ? availableSessions[0]
    : sessions[0];

  const findSession = (date: Date) => {
    const formattedDate = dateService.getFormattedDate(date);
    return sessions.find((s) => {
      const startTime = dateService.getFormattedDate(
        dateService.formatServerDateToLocalDate(s.dateTimeRange.startTime)
      );
      return startTime === formattedDate;
    });
  };

  const getDefaultState = useCallback((): ExperiencePriceWidgetState => {
    const defaultState = {
      date: dateService.formatServerDateToLocalDate(
        firstSession.dateTimeRange.startTime
      ),
      ticketCount: 1,
      selectedSession: firstSession,
      selectedExtras: [] as number[],
    };
    const queryParamState = {
      date: getQueryParam('date') as string | number | undefined,
      ticketCount: getQueryParam('ticketCount') as string | undefined,
      selectedExtras: getQueryParam('selectedExtras') as string[] | undefined,
    };
    queryParamState.date = isNaN(Number(queryParamState.date))
      ? queryParamState.date
      : Number(queryParamState.date);

    if (queryParamState.date) {
      const formattedQueryDate = dateService.getFormattedDate(
        new Date(queryParamState.date)
      );
      const sessionFromQuery = sessions.find((s) => {
        const startTime = dateService.getFormattedDate(
          dateService.formatServerDateToLocalDate(s.dateTimeRange.startTime)
        );
        return startTime === formattedQueryDate;
      });

      if (
        sessionFromQuery &&
        dateService
          .formatServerDateToLocalDate(sessionFromQuery.dateTimeRange.startTime)
          .getTime() > Date.now()
      ) {
        defaultState.date = dateService.formatServerDateToLocalDate(
          sessionFromQuery.dateTimeRange.startTime
        );
        defaultState.selectedSession = sessionFromQuery;
      }
    }

    if (
      Number(queryParamState.ticketCount) &&
      !isNaN(Number(queryParamState.ticketCount))
    ) {
      defaultState.ticketCount = Number(queryParamState.ticketCount);
    }

    if (queryParamState.selectedExtras) {
      defaultState.selectedExtras = queryParamState.selectedExtras.map(
        (extra) => Number(extra)
      );
    }

    return {
      date: defaultState.date,
      ticketCount: defaultState.ticketCount,
      selectedSession: defaultState.selectedSession,
      selectedExtras: defaultState.selectedExtras,
    };
  }, []);

  const disabledExceptDates = useMemo(() => {
    return availableSessions
      .filter((s) => s.availableCapacity > 0)
      .map((s) =>
        dateService.getFormattedDate(
          dateService.formatServerDateToLocalDate(s.dateTimeRange.startTime)
        )
      );
  }, [availableSessions]);

  const dispatchInterceptor = (state: ExperiencePriceWidgetState) => {
    const strippedState = {
      date: dateService.formatDateToServerDateString(state.date),
      ticketCount: state.ticketCount,
      selectedExtras: state.selectedExtras,
    };
    addQueryParam(strippedState);
  };

  const [state, dispatch] = useForm(
    {
      ...getDefaultState(),
    },
    dispatchInterceptor
  );

  const debouncedCalculatePricing = useDebouncedCallback(calculatePricing, 400);

  useEffect(() => {
    debouncedCalculatePricing(state);
  }, [state]);

  useEffect(() => {
    if (state.selectedSession.minimumCapacity > state.ticketCount) {
      dispatch({
        type: 'ticketCount',
        payload: state.selectedSession.minimumCapacity,
      });
    }
  }, [state.selectedSession]);

  const [isSending, setIsSending] = useState<boolean>(false);
  const onSubmit = async () => {
    try {
      setIsSending(true);

      if (state.ticketCount > 0) {
        const foundSession = findSession(state.date);

        if (foundSession) {
          if (foundSession.availableCapacity >= state.ticketCount) {
            const extras: { sessionExtraId: number }[] = [];

            if (state.selectedExtras.length) {
              const selectedSession = state.selectedSession;
              state.selectedExtras.forEach((e) => {
                const foundExtra = selectedSession.extras.find(
                  (a) => a.id === e
                );
                if (foundExtra) {
                  extras.push({
                    sessionExtraId: foundExtra.experienceExtraServiceId,
                  });
                }
              });
            }

            const ticket = await experienceTicketService.createTicket({
              experienceId: experience.id,
              sessionId: foundSession.id,
              ticketCount: state.ticketCount,
              totalPrice:
                calculatedPricing?.totalPrice.amount ??
                state.ticketCount * foundSession.price.amount,
              selectedExtras: extras,
            });

            window.open(
              `${getLocalePrefix(locale)}payment/experience/${ticket.ticketId}`,
              '_self'
            );
          } else {
            toast.error(t('priceWidget.error.capacity'), {
              toastId: 'capacityError',
            });
          }
        }
      }
    } finally {
      setIsSending(false);
    }
  };

  const singlePricingContainerClasses =
    'flex justify-between text-medium text-base mt-3';
  const totalPriceClasses = 'text-18px font-medium';
  const paymentItemLabelClasses = 'text-sm';
  const paymentItemPriceClasses = 'text-sm';

  return (
    <div>
      <div className="header flex justify-between">
        <div className="hourly-price">
          <CurrencyText
            price={
              calculatedPricing?.ticketUnitPrice.amount ??
              state.selectedSession.price.amount
            }
            currency={
              calculatedPricing?.ticketUnitPrice.currency ??
              state.selectedSession.price.currency
            }
            className="text-2xl"
          />
          <span className="price-breakpoint ml-1 inline-block text-lg text-slate-500">
            /{t('common:units.headCount')}
          </span>
        </div>
        <div
          className="comments flex items-center gap-2 underline"
          role="button"
          onClick={() => setCommentModalVisible(true)}
          tabIndex={0}
        >
          <RatingStar
            rating={experience.ratingAverageBundle.overallAverage || 0}
            commentCount={experience.ratingAverageBundle.reviewCount || 0}
          />
        </div>
      </div>
      <div className="mt-3 rounded-2xl border border-slate-500">
        <div className="border-b border-slate-500 px-3 pb-2.5 pt-1">
          <div>
            <label
              className="mb-2 inline-block text-base font-semibold"
              htmlFor="datepicker"
            >
              {t('priceWidget.form.dateLabel')}
            </label>
            <BDatePicker
              sameSizeInMobile
              minDetail="year"
              next2Label={null}
              prev2Label={null}
              calendarClasses="!top-8 -right-3"
              disabledExceptDates={disabledExceptDates}
              inputClasses="text-center naked-input"
              minDate={new Date()}
              showOnLeft
              showTimeInInput={!isMobile}
              value={state.date}
              onChange={(val) => {
                const valDate = dateService.getFormattedDate(val);
                const session = dateToSessionMap[valDate];
                if (session) {
                  dispatch({
                    type: 'selectedSession',
                    payload: session,
                  });
                  dispatch({
                    type: 'date',
                    payload: dateService.formatServerDateToLocalDate(
                      session.dateTimeRange.startTime
                    ),
                  });

                  return dateService.formatServerDateToLocalDate(
                    session.dateTimeRange.startTime
                  );
                }
              }}
            />
          </div>
        </div>
        <div className="px-3 pb-2.5 pt-1">
          <label
            className="mb-2 inline-block text-base font-semibold"
            htmlFor="number-of-people"
          >
            {t('priceWidget.form.ticketCountLabel')}
          </label>
          <BStepperInput
            inputProps={{ id: 'number-of-people' }}
            minLimit={state.selectedSession.minimumCapacity || 1}
            maxLimit={state.selectedSession.availableCapacity}
            inputEnabled
            value={state.ticketCount}
            onChange={(value) =>
              dispatch({
                type: 'ticketCount',
                payload: Number(value),
              })
            }
          />
        </div>
        <div className="flex justify-center pb-2 text-sm">
          <Trans
            i18nKey="experience:priceWidget.form.minimumCapacity"
            values={{ count: state.selectedSession.minimumCapacity }}
            components={{
              b: <strong className="px-1" />,
            }}
          />
        </div>
      </div>
      <div className="flex justify-center py-2">
        {t('priceWidget.form.availableTicketCount')}:&nbsp;
        <b>{state.selectedSession.availableCapacity}</b>
      </div>
      {state.selectedSession.extras.length ? (
        <ModalWithActivator
          modalTitle={t('priceWidget.form.extrasModal.title')}
          activator={
            <BButton
              className="mx-auto mt-4 block text-sm"
              variant="info"
              priority="contained"
              type="button"
            >
              <Confetti color="white" size={16} className="mr-1 inline-block" />{' '}
              {t('priceWidget.form.extrasModal.title')}
            </BButton>
          }
          modalClass="h-screen"
        >
          {state.selectedSession.extras.length ? (
            <PriceWidgetExtraSection
              getChecked={(val) => state.selectedExtras.includes(val)}
              onChange={(isChecked, value) => {
                dispatch({
                  type: 'selectedExtras',
                  payload: value,
                  isAppend: isChecked,
                });
              }}
              list={state.selectedSession.extras.map((extra) => ({
                ...extra,
                unit: ExtraUnitType.HEAD_COUNT,
                price: {
                  amount: extra.price.amount,
                  currency: extra.price.currency,
                  unit: ExtraUnitType.HEAD_COUNT,
                },
              }))}
              getUnits={() => t('common:units.headCount')}
              withPreline={true}
              onUnitChange={noop}
            />
          ) : (
            <PriceWidgetExtrasEmptyView />
          )}
        </ModalWithActivator>
      ) : null}
      {isMobile ? (
        <div className="my-5 rounded-lg border border-primary-500 py-3 px-4">
          <div className="mb-4 font-semibold">{experience.title}</div>
          <div className="mb-4 flex flex-col gap-3">
            <div className="flex items-center gap-2 text-sm">
              <Timer className="flex-shrink-0" size={20} />
              {t('priceWidget.mobile.info.duration')}:{' '}
              {hour ? `${hour} ${t('common:experience.hour')} ` : ''}{' '}
              {minute ? `${minute} ${t('common:experience.minute')} ` : ''}
            </div>
            <div className="flex items-center gap-2 text-sm">
              <ClockClockwise className="flex-shrink-0" size={20} />
              {t('priceWidget.mobile.info.startTime')}:{' '}
              {dateService.getReadableTime(
                dateService.formatServerDateToLocalDate(
                  state.selectedSession.dateTimeRange.startTime
                )
              )}
            </div>
            <div className="flex items-center gap-2 text-sm">
              <MapPin className="flex-shrink-0" size={20} />
              {t('priceWidget.mobile.info.harbor')}: {experience.harbor.label}
            </div>
            <div className="flex items-center gap-2 text-sm">
              <ArrowsClockwise className="flex-shrink-0" size={20} />
              {t('priceWidget.mobile.info.cancellationPolicy', {
                count: experience.cancellationPolicy,
              })}
            </div>
          </div>
          <div className="mb-2 text-sm font-semibold">
            {t('whatsIncluded.title')}
          </div>
          <div className="text-sm">{experience.whatsIncluded}</div>
        </div>
      ) : null}
      <div className="mt-4">
        <div className={singlePricingContainerClasses}>
          <div className={paymentItemLabelClasses}>
            <CurrencyText
              price={
                calculatedPricing?.ticketUnitPrice.amount ??
                state.selectedSession.price.amount
              }
              currency={
                calculatedPricing?.ticketUnitPrice.currency ??
                state.selectedSession.price.currency
              }
            />{' '}
            x {state.ticketCount} {t('common:units.headCount')}
          </div>
          <CurrencyText
            price={
              (calculatedPricing?.ticketUnitPrice.amount ??
                state.selectedSession.price.amount) * state.ticketCount
            }
            currency={
              calculatedPricing?.ticketUnitPrice.currency ??
              state.selectedSession.price.currency
            }
            className={paymentItemPriceClasses}
          />
        </div>
        {calculatedPricing?.extras.length ? (
          <div className={singlePricingContainerClasses}>
            <div className={paymentItemLabelClasses}>
              Toplam Ekstra Hizmet Bedeli
            </div>
            <CurrencyText
              price={
                calculatedPricing?.extras.reduce(
                  (acc, extra) => acc + extra.totalPrice.amount,
                  0
                ) ?? 0
              }
              currency={
                calculatedPricing?.ticketUnitPrice.currency ??
                state.selectedSession.price.currency
              }
              className={paymentItemPriceClasses}
            />
          </div>
        ) : null}
        <BDivider className="mt-3" colorClass="border-primary-900" />
        <div className={singlePricingContainerClasses}>
          <div className={totalPriceClasses}>
            {t('priceWidget.form.pricings.totalAmount')}
          </div>
          <CurrencyText
            price={
              calculatedPricing?.totalPrice.amount ??
              state.selectedSession.price.amount * state.ticketCount
            }
            currency={
              calculatedPricing?.totalPrice.currency ??
              state.selectedSession.price.currency
            }
            className={totalPriceClasses}
          />
        </div>
      </div>
      <BButton className="mt-4" fluid disabled={isSending} onClick={onSubmit}>
        {isSending ? (
          <BSpinnerInline size={24} className="mx-auto" />
        ) : (
          t('priceWidget.form.actions.reserve')
        )}
      </BButton>
    </div>
  );
};

export default ExperiencePriceWidgetBody;
